const CONTRACT_VERSION = 1;
const ACCOUNT_SESSION_SCHEMA_VERSION = 2;
const SESSION_RESPONSE_MAX_BYTES = 8 * 1024;
const MAX_ACCOUNT_JSON_BYTES = 5 * 1024 * 1024;
const ACCOUNT_ID_PATTERN = /^acc_[0-9a-f]{32}$/;
const CSRF_PREFIX = "fcsrf1.";
const CSRF_TAG_LENGTH = 43;
const SESSION_PATH = "/api/v1/account/session";
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.account-state-session-repository.v1",
);
const BROWSER_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-account-state-session-repository.v1",
);
const BROWSER_REPOSITORY_SLOT = Symbol.for(
  "fishystuff.browser-account-state-session-repository.slot.v1",
);

export class AccountStateSessionContractError extends Error {
  constructor(message) {
    super(message);
    this.name = "AccountStateSessionContractError";
  }
}

function invalid(message) {
  throw new AccountStateSessionContractError(message);
}

export function isTransientSessionStatus(status) {
  return [408, 429, 502, 503, 504].includes(status);
}

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactFields(value, expected) {
  if (!isRecord(value)) return false;
  const actual = Object.keys(value).sort();
  const fields = expected.slice().sort();
  return actual.length === fields.length
    && actual.every((field, index) => field === fields[index]);
}

function base64UrlValue(character) {
  const code = character.charCodeAt(0);
  if (code >= 65 && code <= 90) return code - 65;
  if (code >= 97 && code <= 122) return code - 97 + 26;
  if (code >= 48 && code <= 57) return code - 48 + 52;
  if (character === "-") return 62;
  if (character === "_") return 63;
  return -1;
}

function isCanonicalCsrfToken(value) {
  if (typeof value !== "string" || !value.startsWith(CSRF_PREFIX)) {
    return false;
  }
  const tag = value.slice(CSRF_PREFIX.length);
  if (tag.length !== CSRF_TAG_LENGTH) return false;
  for (const character of tag) {
    if (base64UrlValue(character) < 0) return false;
  }
  return (base64UrlValue(tag.at(-1)) & 0x03) === 0;
}

export function decodeAccountMutationAuthorizationV1(value) {
  if (!isRecord(value) || typeof value.kind !== "string") {
    invalid("Account mutation authorization is invalid.");
  }
  if (value.kind === "cookieCsrf") {
    if (!hasExactFields(value, ["kind", "token"]) || !isCanonicalCsrfToken(value.token)) {
      invalid("Account mutation authorization is invalid.");
    }
    return Object.freeze({ kind: "cookieCsrf", token: value.token });
  }
  if (value.kind === "explicitCredential") {
    if (!hasExactFields(value, ["kind"])) {
      invalid("Account mutation authorization is invalid.");
    }
    return Object.freeze({ kind: "explicitCredential" });
  }
  invalid("Account mutation authorization is unsupported.");
}

export function decodeAccountStateSessionV2(value) {
  if (
    !hasExactFields(value, [
      "schemaVersion",
      "accountId",
      "mutationAuthorization",
    ])
    || value.schemaVersion !== ACCOUNT_SESSION_SCHEMA_VERSION
    || typeof value.accountId !== "string"
    || !ACCOUNT_ID_PATTERN.test(value.accountId)
  ) {
    invalid("Account session response is invalid.");
  }
  return Object.freeze({
    kind: "ready",
    accountId: value.accountId,
    mutationAuthorization: decodeAccountMutationAuthorizationV1(value.mutationAuthorization),
  });
}

export async function readBoundedAccountJson(response, label, maxBytes) {
  if (
    typeof label !== "string"
    || label.length === 0
    || !Number.isSafeInteger(maxBytes)
    || maxBytes <= 0
    || maxBytes > MAX_ACCOUNT_JSON_BYTES
  ) {
    throw new TypeError("A bounded account JSON read requires a label and valid byte limit.");
  }
  const contentType = String(response?.headers?.get?.("content-type") ?? "").toLowerCase();
  if (!/^application\/json(?:\s*;|$)/.test(contentType)) {
    invalid(`${label} did not return JSON.`);
  }
  const reader = response?.body?.getReader?.();
  if (!reader) invalid(`${label} response body is unavailable.`);

  const decoder = new TextDecoder("utf-8", { fatal: true });
  let byteLength = 0;
  let text = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      byteLength += value.byteLength;
      if (byteLength > maxBytes) {
        await reader.cancel("account JSON response exceeded its byte limit");
        invalid(`${label} exceeded its byte limit.`);
      }
      text += decoder.decode(value, { stream: true });
    }
    text += decoder.decode();
  } catch (error) {
    if (error instanceof AccountStateSessionContractError) throw error;
    invalid(`${label} could not be decoded.`);
  }

  try {
    return JSON.parse(text);
  } catch (_error) {
    invalid(`${label} returned malformed JSON.`);
  }
}

export function accountMutationHeaders(state) {
  if (!isRecord(state) || state.kind !== "ready") {
    throw new TypeError("Account mutation headers require a ready session state.");
  }
  const common = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (state.mutationAuthorization?.kind === "cookieCsrf") {
    if (!isCanonicalCsrfToken(state.mutationAuthorization.token)) {
      throw new TypeError("Account mutation authorization is invalid.");
    }
    return Object.freeze({
      ...common,
      "x-fishystuff-csrf": state.mutationAuthorization.token,
    });
  }
  if (state.mutationAuthorization?.kind === "explicitCredential") {
    return Object.freeze(common);
  }
  throw new TypeError("Account mutation authorization is invalid.");
}

function sessionUrlFromApiBaseUrl(apiBaseUrl) {
  let url;
  try {
    url = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    throw new TypeError("Account session API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(url.protocol)
    || url.username
    || url.password
    || url.pathname !== "/"
    || url.search
    || url.hash
  ) {
    throw new TypeError("Account session API base URL must be an HTTP origin.");
  }
  return `${url.origin}${SESSION_PATH}`;
}

function freezeState(state) {
  return Object.freeze({ ...state });
}

export function createAccountStateSessionRepository({
  apiBaseUrl,
  eventTarget = null,
  fetchImpl = globalThis.fetch,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Account session fetch implementation is unavailable.");
  }
  const sessionUrl = sessionUrlFromApiBaseUrl(apiBaseUrl);
  let currentState = freezeState({ kind: "uninitialized" });
  let currentRequest = 0;
  let abortController = null;
  let loadPromise = null;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(new Error("Account session subscriber failed."));
    }
  }

  function publish(nextState) {
    if (disposed) return currentState;
    const previousState = currentState;
    currentState = freezeState(nextState);
    const sameAuthority = previousState.kind === currentState.kind
      && (
        currentState.kind !== "ready"
        || previousState.accountId === currentState.accountId
      );
    if (sameAuthority) return currentState;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  async function performLoad() {
    if (disposed) return currentState;
    currentRequest += 1;
    const request = currentRequest;
    abortController?.abort();
    const requestController = new AbortController();
    let retryable = false;
    abortController = requestController;
    if (currentState.kind === "uninitialized") {
      publish({ kind: "loading" });
    }

    try {
      const response = await fetchImpl(sessionUrl, {
        method: "GET",
        cache: "no-store",
        credentials: "include",
        headers: { Accept: "application/json" },
        redirect: "error",
        signal: requestController.signal,
      }).catch((error) => {
        retryable = error instanceof TypeError;
        throw error;
      });
      if (disposed || request !== currentRequest) return currentState;
      if (response.status === 204) return publish({ kind: "signedOut" });
      if (!response.ok) {
        retryable = isTransientSessionStatus(response.status);
        throw new Error("Account session request failed.");
      }
      const decoded = decodeAccountStateSessionV2(
        await readBoundedAccountJson(
          response,
          "Account session",
          SESSION_RESPONSE_MAX_BYTES,
        ),
      );
      if (disposed || request !== currentRequest) return currentState;
      return publish(decoded);
    } catch (error) {
      if (
        disposed
        || request !== currentRequest
        || requestController.signal.aborted
        || error?.name === "AbortError"
      ) {
        return currentState;
      }
      return publish({ kind: "unavailable", reason: "session-request-failed", retryable });
    } finally {
      if (request === currentRequest) abortController = null;
    }
  }

  function startLoad() {
    if (disposed) return Promise.resolve(currentState);
    if (loadPromise) return loadPromise;
    const pending = performLoad();
    loadPromise = pending;
    void pending.then(
      () => {
        if (loadPromise === pending) loadPromise = null;
      },
      () => {
        if (loadPromise === pending) loadPromise = null;
      },
    );
    return pending;
  }

  function load() {
    if (!["uninitialized", "loading"].includes(currentState.kind)) {
      return Promise.resolve(currentState);
    }
    return startLoad();
  }

  async function refresh() {
    if (disposed) return currentState;
    const activeLoad = loadPromise;
    if (activeLoad) await activeLoad;
    return startLoad();
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Account session listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  function handlePageShow(event) {
    if (!disposed && event?.persisted === true) void refresh();
  }

  eventTarget?.addEventListener?.("pageshow", handlePageShow);

  function dispose() {
    if (disposed) return;
    disposed = true;
    currentRequest += 1;
    abortController?.abort();
    abortController = null;
    loadPromise = null;
    listeners.clear();
    eventTarget?.removeEventListener?.("pageshow", handlePageShow);
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: CONTRACT_VERSION,
    dispose,
    load,
    refresh,
    state,
    subscribe,
  });
}

function existingBrowserRepository(windowRef) {
  const repository = windowRef[BROWSER_REPOSITORY_SLOT];
  if (repository === undefined) return null;
  if (
    repository?.[BROWSER_REPOSITORY_BRAND] !== CONTRACT_VERSION
    || !["load", "refresh", "state", "subscribe"].every(
      (method) => typeof repository[method] === "function",
    )
  ) {
    throw new TypeError("The shared browser account session repository is incompatible.");
  }
  return repository;
}

export function browserAccountStateSessionRepository() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") {
    throw new TypeError("The browser account session repository requires a window.");
  }
  const existing = existingBrowserRepository(windowRef);
  if (existing) return existing;

  const owner = createAccountStateSessionRepository({
    apiBaseUrl: windowRef.__fishystuffApiBaseUrl,
    eventTarget: windowRef,
  });
  const repository = Object.freeze({
    [BROWSER_REPOSITORY_BRAND]: CONTRACT_VERSION,
    load: owner.load,
    refresh: owner.refresh,
    state: owner.state,
    subscribe: owner.subscribe,
  });
  Object.defineProperty(windowRef, BROWSER_REPOSITORY_SLOT, {
    configurable: false,
    enumerable: false,
    value: repository,
    writable: false,
  });
  void owner.load();
  return repository;
}

export function refreshBrowserAccountStateSessionRepository() {
  const hadCurrentRepository = Boolean(
    globalThis.window && existingBrowserRepository(globalThis.window),
  );
  const repository = browserAccountStateSessionRepository();
  if (!hadCurrentRepository) return repository.load();
  return repository.refresh();
}
